<template>
  <Tooltip :tooltip-text="t('tooltip_force_mic')" :tooltip-visible="isVisibleTooltip">
    <ButtonTemplateMicInput
      class="primary-mic-wrap"
      :set-ref-button="(el) => (targetRef = el)"
      :text-button="micButtonText"
      :is-data-off="!microphoneStore.isMicOn && !isMicTurningOn"
      @click="toggleMic"
    >
      <template #icon-handler>
        <div class="button-mic-icon-wrapper">
          <component
            :is="microphoneStore.isMicOn ? MicIcon : MicOffIcon"
            :class="{ 'mic-turning-on': isMicTurningOn }"
          />
          <div class="recording">
            <div
              class="record-circle-dot"
              :class="{
                'dot-turning-on': isMicTurningOn,
                'dot-mic-on': microphoneStore.isMicOn,
              }"
            ></div>
            <div
              v-if="microphoneStore.isMicOn"
              class="record-circle"
              :style="{
                width: microphoneStore.outerRadius + 'rem',
                height: microphoneStore.outerRadius + 'rem',
              }"
            />
          </div>
        </div>
      </template>
    </ButtonTemplateMicInput>
  </Tooltip>
</template>

<script setup lang="ts">
import { computed, onUnmounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import ButtonTemplateMicInput from '@/components/domains/chat/common/ButtonTemplateMicInput.vue';
import MicIcon from '@/components/icons/IconMic.vue';
import MicOffIcon from '@/components/icons/IconMicOff.vue';
import Tooltip from '@/components/shared/composite/Tooltip.vue';
import useMicControl from '@/composables/shared/mic/process/useMicControl';
import useMicSignal from '@/composables/shared/mic/process/useMicSignal';
import { useGtag } from '@/composables/shared/useGtag';
import { useAudioStore } from '@/stores/audio';
import { useMessageStore } from '@/stores/message';
import { useMicrophoneStore } from '@/stores/microphone';
import { useSocketsStore } from '@/stores/sockets';
import { useToastStore } from '@/stores/toast.js';
import { UserType, useUserStore } from '@/stores/user';
import eventBus from '@/utils/eventBus';
import { eventKeys } from '@/utils/eventKeys';

const microphoneStore = useMicrophoneStore();
const toastStore = useToastStore();
const audioStore = useAudioStore();
const userStore = useUserStore();
const socketsStore = useSocketsStore();

const { t } = useI18n();
const isMicTurningOn = ref<boolean>(false);

const props = withDefaults(
  defineProps<{
    checkAudio?: boolean;
    signalMode?: boolean;
    muteToastKey?: string;
  }>(),
  {
    checkAudio: false,
    signalMode: false,
    muteToastKey: '',
  }
);

const { startMic, stopMic } = useMicControl();
const { startMicrophone, stopMicrophone } = useMicSignal('MIC');
const messageStore = useMessageStore();
const { trackEventClick } = useGtag();

const isVisibleTooltip = computed<boolean>(() => {
  const { userType = '' } = userStore.userInfo || {};
  return UserType.isUser(userType) && audioStore.isMutedByEmployee;
});

const micButtonText = computed<string>(() => {
  if (isMicTurningOn.value) {
    return t('turning_mic_on');
  }
  return microphoneStore.isMicOn ? t('mic_on') : t('mic_off');
});

const isValidMic = () => {
  if (isMicTurningOn.value) {
    return false;
  }

  if (audioStore.isMutedByEmployee && props.muteToastKey) {
    toastStore.warn(t(props.muteToastKey));
    return false;
  }

  return true;
};

const micEvent = computed(() => {
  return props.signalMode ? { start: startMicrophone, stop: stopMicrophone } : { start: startMic, stop: stopMic };
});

const setMicActivated = () => {
  if (!messageStore.status.isMicActivated) {
    messageStore.patchStatus('isMicActivated', true);
  }
};

const toggleMic = async () => {
  if (!isValidMic()) {
    return;
  }
  isMicTurningOn.value = true;
  setMicActivated();

  if (props.checkAudio && [1, 2].includes(audioStore.playState) && !microphoneStore.isMicOn) {
    eventBus.emit(eventKeys.MIC_ON);
  }
  if (microphoneStore.isMicOn) {
    await micEvent.value.stop();
    trackEventClick('chat', '마이크 끔');
  } else {
    await micEvent.value.start();
    trackEventClick('chat', '마이크 켬');
  }

  isMicTurningOn.value = false;
};

onMounted(() => {
  microphoneStore.initMicErrorEvent(micEvent.value.stop);
});

onUnmounted(async () => {
  await microphoneStore.initMicrophoneStore();
});
</script>

<style scoped lang="scss">
:root[data-theme='light'],
:root[data-theme='dark'] {
  .primary-mic-wrap {
    :deep(.button-mic)[data-off='false'] {
      background-color: $color-navy-100;
      box-shadow: none;
      svg {
        fill: #fff;
        color: #fff;
      }
    }
  }
}

.button-mic-icon-wrapper {
  position: relative;
  display: flex;
  height: 1.75rem;
  align-items: start;
  justify-content: space-between;
}

.recording {
  position: relative;
  display: block;
  width: 2rem;
  height: 1.75rem;
  left: 0.75rem;
}

.record-circle {
  position: absolute;
  background: var(--background-dot);
  border-radius: 50%;
  animation: pulse 1s infinite;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  opacity: 0.5;
}

.record-circle-dot {
  width: 0.5rem;
  height: 0.5rem;
  background: var(--dot);
  border-radius: 50%;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 999;
}

.record-circle-dot.dot-turning-on {
  opacity: 0.3;
}

.record-circle-dot.dot-mic-on {
  background: var(--dot-active);
}

::v-deep(.button-mic) {
  align-items: center;
  justify-content: flex-start;
  min-width: 5rem;
}
</style>
